// The numbers behind the orders page's stats card. Pure, and instance- and
// date-parameterised, so tests can pin both (see itemTypes.ts for why a
// function that reads INSTANCE directly can only ever be tested as Firefleet).
import { parseDate } from '../utils/date-utils';
import { itemTypesFor } from './itemTypes';
import type { OrderStat } from './itemTypes';
import type { Instance } from '../instance';
import type { SalesOrder } from '../Customers/types';

export type StatTotals = Record<OrderStat, number>;

const zeroTotals = (): StatTotals => ({ drones: 0, batteries: 0, chargers: 0, ground: 0 });

export function statTotalsFor(
  inst: Instance,
  lines: Array<{ item_type: string; quantity: number }>,
): StatTotals {
  const statOf = new Map<string, OrderStat>();
  for (const t of itemTypesFor(inst)) if (t.stat) statOf.set(t.value, t.stat);

  const totals = zeroTotals();
  for (const line of lines) {
    const stat = statOf.get(line.item_type);
    if (stat) totals[stat] += line.quantity || 0;
  }
  return totals;
}

/** Days from `today` to `due`, both YYYY-MM-DD: negative is late, 0 is today. */
export function daysFromToday(due: string, today: string): number {
  return parseDate(due)!.diff(parseDate(today)!, 'day');
}

/** "in 4d", "today", "118d late". Short on purpose: it sits beside a date. */
export function relativeDays(days: number): string {
  if (days === 0) return 'today';
  return days > 0 ? `in ${days}d` : `${-days}d late`;
}

export interface DueOrder {
  order: SalesOrder;
  due: string;
  /** Days from today to `due`: negative is late, 0 is due today. */
  daysUntil: number;
}

export interface OrderStats {
  count: number;
  totals: StatTotals;
  /** Orders in one of the tab's stages whose due date has passed. */
  overdue: number;
  /** The soonest order in a stage that is not yet late. */
  nextDue: DueOrder | null;
}

/**
 * Totals over exactly the rows the orders table is showing. Counts and item
 * totals cover every row; overdue and next due look only at rows still in one
 * of the tab's `stages`, because a shipped order's past date is history.
 */
export function orderStatsFor(
  inst: Instance,
  orders: SalesOrder[],
  stages: string[],
  dueField: 'delivery_date' | 'effective_date',
  today: string,
): OrderStats {
  const totals = zeroTotals();
  let overdue = 0;
  let nextDue: DueOrder | null = null;

  for (const order of orders) {
    const t = statTotalsFor(inst, order.lines || []);
    for (const key of Object.keys(totals) as OrderStat[]) totals[key] += t[key];

    const due = order[dueField];
    if (!due || !stages.includes(order.status)) continue;
    const daysUntil = daysFromToday(due, today);
    if (daysUntil < 0) overdue += 1;
    else if (!nextDue || daysUntil < nextDue.daysUntil) nextDue = { order, due, daysUntil };
  }

  return { count: orders.length, totals, overdue, nextDue };
}
